<template>
  <div class="input-group">
    <v-row>
      <v-col cols="12" sm="12" md="12" lg="12">
        <v-text-field
          id="prompt-name"
          v-model="node.name"
          label="Name"
          dense
          hide-details
        >
        </v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="12" md="12" lg="12" class="d-flex v-middle">
        <div>Input type</div>
        <v-radio-group
          v-model="inputType"
          class="m-l-15 m-t-0"
          dense
          hide-details
          row
        >
          <v-tooltip left :max-width="350">
            <template v-slot:activator="{ on }">
              <v-radio v-on="on" label="Voice" value="voice"></v-radio>
            </template>
            <span>
              Accept only voice input
            </span>
          </v-tooltip>
          <v-tooltip left :max-width="350">
            <template v-slot:activator="{ on }">
              <v-radio v-on="on" label="Hybrid" value="hybrid"></v-radio>
            </template>
            <span>
              Accept both voice and DTMF input. <br /><br />
              Applicable to True Voice ASR and Google grammars
            </span>
          </v-tooltip>
          <v-tooltip left :max-width="350">
            <template v-slot:activator="{ on }">
              <v-radio v-on="on" label="DTMF" value="dtmf"></v-radio>
            </template>
            <span>
              Accept only DTMF input. Allow users to press 0-9 keys on phones to
              collect numeric values.
            </span>
          </v-tooltip>
        </v-radio-group>
      </v-col>
    </v-row>

    <div class="m-t-15" v-if="inputType != 'voice'">
      <config-dtmf :dtmf_setting="property.dtmf_setting"></config-dtmf>
    </div>

    <div class="m-t-15">
      <v-switch
        v-model="property.config.default_input.is_customize_input"
        label="Custom setting"
        inset
        dense
        x-small
        hide-details
      ></v-switch>

      <div v-if="property.config.default_input.is_customize_input">
        <div v-if="inputType != 'dtmf'" class="m-t-15">
          <config-asr
            :defaultInput="property.config.default_input"
            :filterConfig="filterConfig"
          ></config-asr>
        </div>

        <div class="m-t-15">
          <config-nlu
            :defaultInput="property.config.default_input"
          ></config-nlu>
        </div>
      </div>

      <div
        class="m-t-15"
        v-if="property.config.default_input.is_customize_input"
      >
        <div class="input-title">Input</div>
        <v-row>
          <v-col cols="12" sm="12" md="12" lg="12" class="p-t-0 p-b-0">
            <v-checkbox
              v-model="property.config.default_input.is_count_retry"
              label="+1 retry if there is no input"
              hide-details
            ></v-checkbox>
          </v-col>
        </v-row>
      </div>
    </div>

    <v-row v-if="isMetaverse">
      <v-col cols="12" sm="12" md="12" lg="12">
        <div class="m-b-5">Response</div>
        <v-textarea
          id="description"
          class="textarea-input"
          v-model="property.responseText"
          label=""
          rows="4"
          solo
          hide-details
        ></v-textarea>
      </v-col>
    </v-row>

    <hr class="note-divider" />
    <note-component :node="node" />
  </div>
</template>

<script>
import _ from 'lodash'
import apiEntity from '@/apis/entity'
import apiGrammar from '@/apis/grammar'

import NoteComponent from '../../../components/note'
import ConfigAsr from '../start/components/config_asr'
import ConfigNlu from '../start/components/config_nlu'
import ConfigDtmf from '../start/components/config_dtmf'

export default {
  props: {
    node: Object,
    start: Object
  },
  components: {
    NoteComponent,
    ConfigAsr,
    ConfigNlu,
    ConfigDtmf
  },
  data() {
    return {
      property: {},
      responseText: '',
      grammars: [],
      isMetaverse: process.env.VUE_APP_METAVERSE === 'true',
      filterConfig: 'google',
      inputType: 'voice',
      grammarsFromApi: []
    }
  },
  watch: {
    'property.grammar_name': {
      immediate: true,
      handler(val) {
        this.findGrammar()
        if (this.grammars.length > 0) this.setGrammarId()
      }
    },
    inputType: {
      immediate: true,
      handler(val) {
        this.property.input_type = val
      }
    }
  },
  async created() {
    if (
      !this.node.property.config.default_input.asr ||
      (this.node.property.config.default_input.asr &&
        Object.entries(this.node.property.config.default_input.asr).length ===
          0)
    ) {
      if (this.start.data.property.default_input.asr) {
        this.node.property.config.default_input.asr = Object.assign(
          {},
          this.start.data.property.default_input.asr
        )
      }
      this.node.property.config.default_input.asr = Object.assign(
        {
          timeout: '5.00',
          complete_timeout: '1.00',
          incomplete_timeout: '1.50',
          max_speech_timeout: '14.00',
          asr_confidence_threshold: '0.00',
          voice_barge_in: false,
          voice_barge_in_sensitivity: 4,
          voice_barge_in_threshold: 128
        },
        this.node.property.config.default_input.asr
      )
    }

    if (!this.node.property.config.default_input.is_customize_input) {
      if (!this.node.property.config.default_input.nlu)
        if (this.start.data.property.default_input.nlu) {
          this.node.property.config.default_input.nlu = this.start.data.property.default_input.nlu
        }
      if (this.node.property.config.default_input.is_count_retry === undefined)
        this.node.property.config.default_input.is_count_retry = this.start.data.property.default_input.is_count_retry
    }

    if (this.node.property.input_type) {
      this.inputType = this.node.property.input_type
    }

    if (this.node.property.dtmf_setting === undefined) {
      this.node.property.dtmf_setting = {
        terminator_length: 4,
        terminator_timeout: '5.00',
        terminator_char: '#',
        dtmf_barge_in: false
      }
    }

    if (this.node.property.grammar_name) {
      if (!this.node.property.grammar_id) this.node.property.grammar_id = ''
    }

    this.property = this.node.property
    this.property.responseText = JSON.stringify(this.property.response)

    this.getGrammar()
  },
  methods: {
    async getGrammar() {
      try {
        const getGrammar = await apiGrammar.getGrammar()
        const { data } = getGrammar
        this.grammars = data
        this.grammarsFromApi = data
        this.findGrammar()
      } catch {}
    },
    findGrammar() {
      const findGrammar = _.find(this.grammars, {
        name: this.property.grammar_name
      })
      if (findGrammar) {
        this.filterConfig = findGrammar.type
      } else {
        this.filterConfig = 'google'
      }
    },
    setGrammarId() {
      const grammar = _.find(this.grammars, {
        name: this.property.grammar_name
      })
      if (grammar) this.property.grammar_id = grammar._id
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .v-text-field__slot > input,
::v-deep .v-text-field__slot > .v-select__slot {
  margin: 0px !important;
  padding: 0px !important;
}
.boost-scroll {
  // height: 0px;
  // margin-bottom: 10px;
  width: 150px;
  max-width: 150px;
}
.boost-text {
  text-align: left;
  width: 15px;
}
.font-size-body {
  width: 300px;
  display: flex;
  text-align: justify;
  text-justify: inter-word;
  padding-left: 4px;
  margin-left: 4px;
  padding-right: 4px;
  margin-right: 4px;
  margin-bottom: 0px;
}
.padding-tooltip {
  padding-left: 12px;
  padding-bottom: 0px;
  margin-right: 12px;
}
.col {
  flex-basis: auto;
  display: block;
}
</style>
